// Firebase client initialization scaffolding.
//
// Wires up the five Firebase services ShareBite depends on: Authentication
// (Google Sign-In only), Cloud Firestore, Storage, Cloud Functions, and Cloud
// Messaging (FCM). No business logic lives here; feature wiring is added in
// later tasks (Section 13/14). Configuration is supplied via Vite environment
// variables (see .env.example).
import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getMessaging, isSupported, type Messaging } from "firebase/messaging";

// Configuration is supplied via Vite environment variables (see .env.example).
// When a variable is absent (e.g. running the interactive demo without a
// configured project), we fall back to a harmless placeholder so module
// initialization never throws `auth/invalid-api-key` and the app can still
// boot. Real Auth/Firestore calls will fail gracefully until valid keys are
// provided — sign-in surfaces an error rather than crashing the page.
function envOr(value: string | undefined, fallback: string): string {
  return value && value.length > 0 ? value : fallback;
}

const firebaseConfig = {
  apiKey: envOr(import.meta.env.VITE_FIREBASE_API_KEY, "demo-api-key"),
  authDomain: envOr(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, "demo.firebaseapp.com"),
  projectId: envOr(import.meta.env.VITE_FIREBASE_PROJECT_ID, "demo-sharebite"),
  storageBucket: envOr(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET, "demo-sharebite.appspot.com"),
  messagingSenderId: envOr(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID, "0000000000"),
  appId: envOr(import.meta.env.VITE_FIREBASE_APP_ID, "1:0000000000:web:demo"),
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

// Authentication is restricted to Google Sign-In (Requirement 1).
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// FCM is only available in supported browser contexts; callers must handle
// the null case (e.g., unsupported browsers or denied permission).
export async function getMessagingIfSupported(): Promise<Messaging | null> {
  return (await isSupported()) ? getMessaging(app) : null;
}
